/**
 * The waiting list — one dossier, one line, and the lines are a grid rather than a row of chips.
 *
 * The first version put everything the validated rules asked for on a card, and the two surgeons
 * read it as a wall. The second cut it to five items on one line, which was right, and left them
 * laid out with `flex`: a dossier carrying the delay marker pushed its own name, date and signal
 * count sideways, so no two lines agreed on where anything was. At two dossiers that is invisible.
 * At twenty it is the whole problem — a queue is *scanned* down a column, and a column that moves
 * from line to line cannot be scanned at all.
 *
 * So the columns are fixed and every line lands in the same place, with the delay column left empty
 * rather than closed up. The patient's name sits straight after the badges and carries the weight:
 * the question a surgeon opens this screen with is « which one do I take next », and the answer is a
 * person, not a tag.
 *
 * **Colour stops when the work stops.** Their rule paints a dossier « Prioritaire » in red, and that
 * is right while it is waiting. Once the avis is rendered, red says « act now » about something
 * already dealt with, next to a green pill saying the opposite — so a settled dossier keeps its
 * words and loses its hues. Same reason a counter is only coloured while it counts something.
 *
 * The delay marker stays, alone, beside the priority: their rule makes a dossier « Prioritaire » on
 * one strong signal, and `G2` (« prêt à envisager une opération ») is strong while setting no delay
 * at all. Without it, a red badge and a count of strong signals both say *loud* and neither says
 * *soon*.
 */

import { Link } from "react-router";
import { History, TimerReset, UserRound } from "lucide-react";
import { SelectOrdinal, Trans, useLingui } from "@lingui/macro";
import { cn } from "@app/lib/utils";
import {
  HISTORIQUE_BADGE,
  TAG,
  STATUT_LABELS,
  delaiStyle,
  dossierName,
  formatDate,
  priorityStyle,
  statutStyle,
  type DossierSummary,
} from "@app/utils/teleexpertise";

/**
 * The column track. Named once because the header and every row must agree on it, and two
 * copies of a grid definition drift the first time one of them is edited.
 */
const COLUMNS =
  "md:grid md:grid-cols-[minmax(150px,auto)_74px_minmax(136px,auto)_minmax(0,1fr)_128px_88px] md:items-center";

interface DossierQueueProps {
  dossiers: DossierSummary[];
  /** Where a dossier opens — full screen, on its own route. */
  hrefFor: (dossier: DossierSummary) => string;
}

export function DossierQueue({ dossiers, hrefFor }: DossierQueueProps) {
  const { i18n, t } = useLingui();

  if (dossiers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
        <Trans>Aucun dossier ne correspond à ce filtre.</Trans>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Only the two right-hand columns are labelled. « Prioritaire » and « Lucie ESMINGAUD » say
          what they are; « 3 » and « 16/09/2026 » do not. */}
      <div
        className={cn(
          "hidden gap-x-3 border-b border-slate-100 bg-slate-50/60 px-3 py-2",
          "text-[11px] font-medium uppercase tracking-wide text-slate-400",
          COLUMNS,
        )}
      >
        <span />
        <span />
        <span />
        <span />
        <span className="text-right">
          <Trans>Signaux forts</Trans>
        </span>
        <span className="text-right">
          <Trans>Reçu le</Trans>
        </span>
      </div>

      <ul className="divide-y divide-slate-100">
        {dossiers.map((dossier) => {
          const answered = dossier.triage_statut === "avis_rendu";
          const name = dossierName(dossier);
          // Only when this dossier ARRIVES on top of something. The first of a series carries the
          // same field, and showing it there would put a mark on every line of a pair — a mark that
          // is always present is one a reader stops seeing. The earlier demand says so in its own
          // screen, where there is room to say which way round it goes.
          const repeat = dossier.historique && dossier.historique.rang > 1 ? dossier.historique : null;
          const precedent = repeat?.precedent;
          return (
            <li key={dossier.dossier_id}>
              <Link
                to={hrefFor(dossier)}
                className={cn(
                  "flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 transition-colors",
                  "hover:bg-primary/5",
                  // Settled work stays readable and stops competing: what a colleague already did
                  // must be visible, just not first.
                  answered && "bg-slate-50/40",
                  COLUMNS,
                )}
              >
                <span
                  className={cn(
                    TAG, "justify-self-start",
                    priorityStyle(dossier),
                  )}
                >
                  {/* Their vocabulary, shown verbatim when it is one we do not know. */}
                  {dossier.priorite_libelle ?? dossier.priorite_proposee ?? t`Sans priorité`}
                </span>

                {/* Kept as an empty cell rather than closed up, so the columns after it hold. */}
                <span className="justify-self-start">
                  {dossier.urgence_clinique && (
                    <span
                      className={cn(
                        TAG,
                        delaiStyle(dossier),
                      )}
                      title={t`Au moins un signal fort fixe un délai — pas seulement une intention`}
                    >
                      <TimerReset className="h-3 w-3" />
                      <Trans>Délai</Trans>
                    </span>
                  )}
                </span>

                <span
                  className={cn(
                    TAG, "justify-self-start",
                    statutStyle(dossier.triage_statut),
                  )}
                >
                  {i18n._(STATUT_LABELS[dossier.triage_statut])}
                </span>

                {/* The mark rides with the NAME rather than taking a column of its own: it is a
                    fact about the person, and this is the one flexible track — so a long name
                    truncates and the mark survives, instead of the mark pushing the name off. */}
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      "min-w-0 truncate text-sm font-medium",
                      answered ? "text-slate-600" : "text-slate-900",
                    )}
                  >
                    {name ?? (
                      <span className="inline-flex items-center gap-1 font-normal text-slate-400">
                        <UserRound className="h-3.5 w-3.5" />
                        <Trans>Patient non identifié</Trans>
                      </span>
                    )}
                  </span>
                  {repeat && (
                    <span
                      className={cn(TAG, HISTORIQUE_BADGE, "shrink-0")}
                      title={
                        precedent
                          ? t`Demande précédente reçue le ${formatDate(precedent.recu_le, i18n.locale) ?? "?"} — ${i18n._(STATUT_LABELS[precedent.triage_statut])}`
                          : t`Ce patient a déjà adressé une demande`
                      }
                    >
                      <History className="h-3 w-3" />
                      <SelectOrdinal
                        value={repeat.rang}
                        one="#ʳᵉ demande"
                        other="#ᵉ demande"
                      />
                    </span>
                  )}
                </span>

                {/* Only emphasised while it counts something: a bold « 0 » teaches a reader to stop
                    looking at the column. */}
                <span
                  className={cn(
                    "text-xs tabular-nums md:text-right",
                    dossier.signaux_forts > 0 && !answered
                      ? "font-semibold text-slate-900"
                      : "text-slate-400",
                  )}
                >
                  {dossier.signaux_forts > 0 ? (
                    <Trans>{dossier.signaux_forts} signaux forts</Trans>
                  ) : (
                    <Trans>Aucun</Trans>
                  )}
                </span>

                <span className="text-xs tabular-nums text-slate-500 md:text-right">
                  {formatDate(dossier.recu_le, i18n.locale) ?? t`Date inconnue`}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
